/**
 * STITCH-2 -- lazy LeadQuickView host for row surfaces that hold only a lead
 * id (Workboard rows, Today follow-up rows). Fetches the SAME role-projected
 * workspace read the canonical detail page uses (one truth; the backend
 * enforces role + org + clinic scope and the role-aware projection), finds
 * the record, and renders the ONE LeadQuickView. A record outside the
 * caller's scope page renders the honest not-found state -- never a
 * fabricated view.
 *
 * CALL-CENTER-LEAD-360-SALESFORCE-EXPERIENCE-V1 (Mission 10): the host now
 * also loads the config-backed disposition vocabulary (fail-soft) and
 * injects the ONE disposition panel into the drawer for callers whose role
 * lawfully holds the workspace surface (me.leadsWorkspaceEnabled -- the
 * same gate as "Open full view"; the server re-validates every save). A
 * failed vocabulary read or an out-of-audience caller renders the drawer
 * byte-identically with no panel -- never a dead door.
 *
 * MANAGER-LEAD-READ-ACCESS-CORRECTIVE-V1 (SEE-NOT-DO): the panel gate is now
 * TWO conditions, not one. `me.leadsWorkspaceEnabled` is the READ signal, and
 * since the 2026-07-28 ruling it is present for gm_agm / regional_head too --
 * but the disposition SAVE is refused for them server-side
 * (requireLeadsWorkspaceActionScope). So the panel additionally requires
 * `canActOnLeadsWorkspace(me.role)`; a manager gets the drawer, the record,
 * and "Open full view", and NO disposition panel (no dead door, LES-016).
 */

import { useEffect, useState, type ReactNode } from 'react';

import {
  fetchLeadDispositionOptions,
  fetchLeadsWorkspace,
  fetchMyWorkActions,
  saveLeadDisposition,
  type LeadDispositionOptionsResponse,
} from '@/dashboard/api';
import { useAuth } from '@/auth/auth-context';
import type { FollowupRow } from '@/dashboard/workboard/workboard-view';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { AcButton, Detail, DrawerShell, EmptyState, HighlightsPanel, LoadingState } from '@/dashboard/ui';
import LeadQuickView, { type LeadQuickViewFrame } from '@/dashboard/leads/LeadQuickView';
import LeadDispositionPanel from '@/dashboard/leads/LeadDispositionPanel';
import { canActOnLeadsWorkspace } from '@/dashboard/leads/lead-list-contract';
import {
  buildQuickViewFromLead360,
  type LeadQuickViewModel,
} from '@/dashboard/leads/lead-quickview-view';

export const QUICKVIEW_HOST_LIMIT = 100;

export interface LeadQuickViewHostProps {
  readonly leadId: number;
  /** Reception details re-read this existing assigned task, never the full lead list. */
  readonly assignedTaskId?: number;
  /** Row-known display name for the loading shell (honest fallback). */
  readonly seedTitle: string;
  readonly onClose: () => void;
  /** Docked hosts keep their Close control disabled through a disposition write. */
  readonly onBusyChange?: (busy: boolean) => void;
  /** Hosting list refetch after a successful owner/stage change. */
  readonly onChanged?: () => void;
  /** SCOPE 5 stage-aware strip built by the host from the loaded record;
   *  `reload` refetches this drawer's record so the strip re-derives. */
  readonly stageStrip?: (model: LeadQuickViewModel, reload: () => void) => ReactNode;
  /**
   * ADORACARE-VISUAL-SYSTEM-V1: the frame the loaded view renders in --
   * 'drawer' (default; every existing host unchanged) or 'panel' (the
   * persistent context panel inside a work plane). The read, the audience
   * gates and the disposition writer are IDENTICAL in both frames.
   */
  readonly frame?: LeadQuickViewFrame;
  /** Context-only entry points omit all local writers. Existing hosts retain their actions. */
  readonly contextOnly?: boolean;
  /** MY-WORK-PRIMITIVE-CONFORMANCE-V6: the hosting surface's own note,
   *  rendered inside the loaded view's reference region (never fetched). */
  readonly children?: ReactNode;
}

type HostState =
  | { kind: 'loading' }
  | { kind: 'ready'; model: LeadQuickViewModel }
  | { kind: 'missing' }
  | { kind: 'error'; message: string };

export default function LeadQuickViewHost(props: LeadQuickViewHostProps): JSX.Element {
  const me = useDashboardMeContext()?.me;
  if (!me) return <LoadingState label="Loading lead details..." lines={4} />;
  return me.role === 'receptionist' ? <AssignedTaskQuickView {...props} /> : <WorkspaceLeadQuickViewHost {...props} />;
}

function AssignedTaskQuickView({leadId, assignedTaskId, onClose}: LeadQuickViewHostProps): JSX.Element {
  const me = useDashboardMeContext()?.me ?? null;
  const {user, session} = useAuth();
  const key = JSON.stringify([user?.id, session?.access_token, me?.role, me?.organizationId, me?.clinicIds, assignedTaskId, leadId]);
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<{key: string; row?: FollowupRow; error?: boolean} | null>(null);
  useEffect(() => {
    let current = true;
    setResult(null);
    if (!user || me?.role !== 'receptionist' || !Number.isSafeInteger(assignedTaskId) || !Number.isSafeInteger(leadId)) {
      setResult({key, error: true}); return;
    }
    // The existing server reader rechecks organization, clinic and role/self assignment.
    // Match both IDs: a stale row cannot be substituted by another task for this lead.
    void fetchMyWorkActions().then(rows => {
      if (current) setResult({key, row: rows.find(row => row.id === assignedTaskId && row.leadId === leadId)});
    }, () => { if (current) setResult({key, error: true}); });
    return () => { current = false; };
  }, [key, attempt]);
  const visible = result?.key === key ? result : null;
  if (visible?.row && me) {
    const row = visible.row;
    return <DrawerShell title={row.patientName || 'Assigned task'} eyebrow="Assigned task" onClose={onClose}
      testId="lead-quickview" footer={<span>Read-only task details.</span>}>
      <HighlightsPanel label="Task details" facts={[
        {key:'lead',label:'Lead',value:`#${row.leadId}`},
        {key:'status',label:'Status',value:row.status},
        {key:'channel',label:'Channel',value:row.channel},
      ]} />
      <p>{row.reason}</p>
      {row.dueContext ? <p>{row.dueContext}</p> : null}
    </DrawerShell>;
  }
  return <DrawerShell title="Assigned task" onClose={onClose} testId="lead-quickview-host">
    {!visible ? <LoadingState label="Loading lead details..." lines={4} /> : <>
      <EmptyState title={visible.error ? 'Could not load this task.' : 'Task is no longer available.'} />
      <AcButton onClick={() => setAttempt(value => value + 1)}>Try again</AcButton>
    </>}
  </DrawerShell>;
}

function WorkspaceLeadQuickViewHost({
  leadId,
  seedTitle,
  onClose,
  onBusyChange,
  onChanged,
  stageStrip,
  frame = 'drawer',
  contextOnly = false,
  children,
}: LeadQuickViewHostProps): JSX.Element {
  const meCtx = useDashboardMeContext();
  const me = meCtx?.me ?? null;
  const [state, setState] = useState<HostState>({ kind: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);
  const [dispositionOptions, setDispositionOptions] =
    useState<LeadDispositionOptionsResponse | null>(null);

  // READ audience (drawer + "Open full view") vs ACTION audience (the
  // disposition panel). SEE-NOT-DO: the second is a strict subset.
  const inWorkspaceAudience = me?.leadsWorkspaceEnabled === true;
  const canDisposition = !contextOnly && inWorkspaceAudience && canActOnLeadsWorkspace(me?.role);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    fetchLeadsWorkspace(null, QUICKVIEW_HOST_LIMIT)
      .then((data) => {
        if (cancelled) return;
        const record = data.records.find(
          (r) => String(r.safeLeadId) === String(leadId),
        );
        if (record === undefined) {
          setState({ kind: 'missing' });
          return;
        }
        setState({
          kind: 'ready',
          model: buildQuickViewFromLead360(record, Date.now()),
        });
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setState({
            kind: 'error',
            message: e instanceof Error ? e.message : String(e),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, reloadKey]);

  useEffect(() => {
    // Fail-soft vocabulary read (the panel is an enhancement, never a
    // blocker); skipped entirely outside the workspace ACTION audience --
    // this host consumes ONLY `dispositionOptions` from the payload, so a
    // read-only manager caller fires NO vocabulary lookup at all
    // (lookup-never-fires, pinned).
    if (!canDisposition) return undefined;
    let cancelled = false;
    fetchLeadDispositionOptions()
      .then((data) => {
        if (!cancelled) setDispositionOptions(data);
      })
      .catch(() => {
        if (!cancelled) setDispositionOptions(null);
      });
    return () => {
      cancelled = true;
    };
  }, [canDisposition]);

  if (state.kind === 'ready') {
    const model = state.model;
    return (
      <LeadQuickView
        model={model}
        me={me}
        contextOnly={contextOnly}
        frame={frame}
        onClose={onClose}
        onChanged={() => {
          setReloadKey((k) => k + 1);
          onChanged?.();
        }}
        stageStrip={stageStrip?.(model, () => setReloadKey((k) => k + 1))}
        dispositionPanel={
          canDisposition &&
          dispositionOptions !== null &&
          model.safeLeadId !== null ? (
            <LeadDispositionPanel
              vocabulary={dispositionOptions.dispositionOptions}
              save={async (body) => {
                onBusyChange?.(true);
                try { return await saveLeadDisposition(String(model.safeLeadId), body); }
                finally { onBusyChange?.(false); }
              }}
              onSaved={(): void => {
                // The drawer refetches in place (no screen hop); the
                // hosting list re-sorts through the existing callback.
                setReloadKey((k) => k + 1);
                onChanged?.();
              }}
            />
          ) : undefined
        }
      >
        {children ?? null}
      </LeadQuickView>
    );
  }

  const pending =
    state.kind === 'loading' ? (
      frame === 'panel' ? (
        <LoadingState
          label="Loading lead details..."
          lines={4}
          testId="lead-quickview-loading"
        />
      ) : (
        <p className="lead-quickview__loading">Loading lead details&hellip;</p>
      )
    ) : state.kind === 'missing' ? (
      <EmptyState
        title="Not in your current view"
        body="This lead is not in your clinic scope's recent list. Open the full view from a surface that carries it."
      />
    ) : (
      <EmptyState title="Could not load this lead." body={state.message} />
    );

  if (frame === 'panel') {
    // The panel frame's pending shell: the same anatomy the loaded panel
    // renders (head with the row-known name, then the body), so the plane
    // does not jump when the record lands.
    return (
      <Detail
        label={`${seedTitle} context`}
        busy={state.kind === 'loading'}
        testId="lead-quickview-host"
        frame="panel"
        head={<h2 className="ac-detail__title">{seedTitle}</h2>}
      >
        {pending}
      </Detail>
    );
  }

  return (
    <DrawerShell
      eyebrow="Lead quick view"
      title={seedTitle}
      onClose={onClose}
      testId="lead-quickview-host"
    >
      {pending}
    </DrawerShell>
  );
}
